import './App.sass';
import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import io from 'socket.io-client';
import { configuration } from './Configuration';




import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Loader2, MessageSquarePlus, MoreHorizontal, Pin, PinOff, Plus, Search, Trash2, X } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { orderConversations, toggleConversationPinned, usePinnedConversations } from "./chat/pinnedConversations";
import { appendPage, nextPage, pageHasMore, refreshPage } from "./chat/conversationPaging";
import { LoadMoreSentinel } from "./chat/LoadMoreSentinel";
import { MarqueeText } from "./chat/MarqueeText";
import { EnterRow } from "./chat/EnterRow";
import { formatRelativeShort, type Translate } from './utils/relativeTimeShort';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { useAtom } from 'jotai';
import { authenticationAtom } from "./State";
import { userAPI } from './UserAPI';
import { getTaskTriAttrs } from './utils/triAttrs';
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
// import { useCustomerTheme } from './Theme';
import { Conversation, Message, MessageType } from '@the-real-insight/tri-model';
import { ChatExchange } from './components/ChatExchange';
import { useChatSidebarSlot } from '@/components/AppShell';
import { cn } from '@/lib/utils';
import type { CustomerToolOption } from './components/ToolSelector';
import { mapCustomerToolsForChat } from './utils/customerTools';
import { pickNextConversation } from './nextConversation';
import { publishDisplayedConversationRun } from './chatRuns';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useAnimatedSidebarOptional } from '@/components/motion/animated-sidebar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { SharedLayoutBg } from '@/components/motion/shared-layout-bg';
import { SPRING_PRESS } from '@/lib/ease';
import { isEndedProcessConversation } from './processConversationSections';
import { registerAssistantSurface } from '@/guide/morph/assistantSurface';
// removed: tinycolor no longer needed for background styling

/** Orange “process” row: viewer is not the conversation originator (conv.user), or worklist has entries. */
function isProcessConversationForViewer(conv: any, viewerEmail: string | undefined): boolean {
    const hasWorklist = (conv.worklist?.length ?? 0) > 0;
    if (hasWorklist) return true;
    if (!viewerEmail) return false;
    const owner = conv?.user;
    if (owner == null || owner === '') return false;
    return String(owner).toLowerCase() !== String(viewerEmail).toLowerCase();
}

/** Viewer is the conversation owner (conv.user matches signed-in user). */
function isConversationOwner(conv: any, viewerEmail: string | undefined): boolean {
    if (!viewerEmail) return false;
    const owner = conv?.user;
    if (owner == null || owner === '') return false;
    return String(owner).toLowerCase() === String(viewerEmail).toLowerCase();
}

/**
 * Pull the per-instance synopsis from the conversation, if any. The portal
 * receives it via worklist enrichment from tri-server: each worklist task
 * carries the parent process instance's `instanceSynopsis`. When set, it
 * replaces the otherwise-uninformative "first user message" title and lets
 * users tell parallel cases of the same workflow apart.
 */
function getInstanceSynopsisForConversation(conv: any): string | undefined {
    const worklist: any[] = conv?.worklist ?? [];
    for (const t of worklist) {
        const syn = t?.instanceSynopsis;
        if (typeof syn === 'string' && syn.length > 0) return syn;
    }
    return undefined;
}

const ConversationRow = React.memo(function ConversationRow(props: {
    conv: any;
    isSelected: boolean;
    onSelect: (conv: any) => void;
    onDelete: (conv: any) => void;
    /** Pinned in this browser (localStorage) — sorts to the top, shows the pin. */
    pinned: boolean;
    onTogglePin: (conv: any) => void;
    title: string;
    worklistCount: number;
    hasWorklist: boolean;
    /** Name of the BPMN node the conversation currently waits at. */
    currentNode: string;
    showDelete: boolean;
    viewerEmail: string | undefined;
    /** The agent is working on this conversation — several may run at once. */
    isRunning: boolean;
    /** Age of the row, in ms — the same value the list sorts by. */
    timestamp: number | undefined;
}) {
    const { conv, isSelected, onSelect, onDelete, pinned, onTogglePin, title, worklistCount, hasWorklist, currentNode, showDelete, isRunning, timestamp } = props;
    const { t } = useTranslation();
    const reduceMotion = useReducedMotion() ?? false;
    const runningLabel = t('chatBotPage.conversationRunning');
    const synopsis = getInstanceSynopsisForConversation(conv);
    // When a synopsis is available, it becomes the primary descriptor and the
    // original title slides down to a secondary line. Otherwise we render
    // exactly as before.
    const primary = synopsis ?? title;
    const secondary = synopsis ? title : undefined;
    // A process conversation nearly always has exactly one open task, so the
    // count carried no information — where the process stands does. Extra tasks
    // beyond the shown one are kept visible as a "+n" suffix.
    const extraTasks = worklistCount > 1 ? worklistCount - 1 : 0;
    const nodeTooltip = extraTasks > 0 ? `${currentNode} (+${extraTasks})` : currentNode;
    // Statt der Datums-Trenner steht das Alter an der Zeile: „3m", „2h", „5d".
    const relativeAge = formatRelativeShort(timestamp, t as Translate);
    const absoluteAge = timestamp === undefined ? undefined : new Date(timestamp).toLocaleString();
    const handleRowClick = useCallback(() => {
        onSelect(conv);
    }, [conv, onSelect]);
    // While the "…" menu is open the trailing cluster stays expanded, hover or not.
    const [menuOpen, setMenuOpen] = useState(false);
    return (
        <div className='flex items-center' data-testid='chat-conversation-entry'>
        <motion.div
            role="button"
            tabIndex={0}
            data-testid="chat-conversation-row"
            whileTap={reduceMotion ? undefined : { scale: 0.98 }}
            transition={SPRING_PRESS}
            className={cn(
                // Benannte Gruppe: der äußere Rahmen der shadcn-Sidebar trägt
                // selbst ein unbenanntes `group`, ein schlichtes `group-hover:`
                // würde also auf jedes Überfahren der ganzen Sidebar anspringen.
                // Maße und Schriftgrößen wie die Menüzeilen der Settings-
                // Seitenleiste; die Hover-Fläche malt die gleitende Pille von
                // `SharedLayoutBg`, nicht die Zeile selbst.
                'group/row flex min-h-9 min-w-0 grow cursor-pointer select-none items-center gap-2.5 rounded-xl px-3 py-1.5 text-left transition-colors',
                isSelected && 'bg-[var(--oa-surface-2)]',
            )}
            onClick={handleRowClick}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleRowClick(); } }}
        >
            {/* One state symbol per row, left of the title: the ring while the agent works on
                this conversation. The orange gear for an open human task is gone (28.09.2026) —
                the section heading "Prozesse" and the node line already say it. */}
            {isRunning ? (
                <Loader2
                    role='status'
                    aria-live='polite'
                    aria-label={runningLabel}
                    className='h-4 w-4 shrink-0 animate-spin text-[var(--oa-fg-3)] motion-reduce:animate-none'
                />
            ) : null}
            <div className='flex min-w-0 grow flex-col'>
                {/* A cut-off title marquees while the pointer rests on the entry (28.09.2026). */}
                <MarqueeText
                    text={primary}
                    disabled={isSelected}
                    className={cn(
                        'text-sm font-medium',
                        isSelected ? 'text-[var(--oa-fg)]' : 'text-muted-foreground group-hover/row:text-[var(--oa-fg)]',
                    )}
                />
                {secondary && (
                    <div className='truncate text-xs text-[var(--oa-fg-4)]' title={secondary}>
                        {secondary}
                    </div>
                )}
                {hasWorklist && currentNode && (
                    <div
                        className='pointer-events-none truncate text-xs text-[var(--oa-fg-4)]'
                        title={nodeTooltip}
                    >
                        {currentNode}
                        {extraTasks > 0 ? ` +${extraTasks}` : ''}
                    </div>
                )}
            </div>
            {/* The pin is state, not a control: it stays visible at rest. */}
            {pinned && (
                <Pin aria-hidden='true' className='h-3 w-3 shrink-0 text-[var(--oa-fg-4)]' data-testid='chat-conversation-pinned' />
            )}
            {/* Trailing cluster — age and "…" menu (user decision 28.09.2026): at rest it takes
                no width, so the title gets the whole row; on hover, keyboard focus, an open menu
                or for the selected row it grows in (grid column 0fr → 1fr, no measuring) and the
                title yields.
                Pin/unpin is browser-local; delete stays the same action as before. */}
            <div
                className={cn(
                    'chat-conversation-trailing grid shrink-0 grid-cols-[0fr] opacity-0 transition-[grid-template-columns,opacity] duration-200 ease-out motion-reduce:transition-none',
                    'group-hover/row:grid-cols-[1fr] group-hover/row:opacity-100 group-focus-within/row:grid-cols-[1fr] group-focus-within/row:opacity-100',
                    // The selected conversation is locked open: age and menu stay, no marquee.
                    (menuOpen || isSelected) && 'grid-cols-[1fr] opacity-100',
                )}
                data-testid='chat-conversation-trailing'
                // hook for the touch rule in chat-oa.css (no hover there: always open)
            >
                <div className='flex min-w-0 items-center gap-1.5 overflow-hidden'>
                    {relativeAge && (
                        <span
                            className='shrink-0 text-xs tabular-nums text-[var(--oa-fg-4)]'
                            title={absoluteAge}
                            data-testid='chat-conversation-age'
                        >
                            {relativeAge}
                        </span>
                    )}
                    <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
                        <DropdownMenuTrigger asChild>
                            <Button
                                type='button'
                                variant='ghost'
                                size='icon'
                                data-testid='chat-conversation-menu'
                                aria-label={t('chatBotPage.conversationMenu')}
                                // `max-sm:` too: the icon size grows to 44px on small screens, which here spilled over the row.
                                className='h-6 w-6 max-sm:h-6 max-sm:w-6 shrink-0 text-[var(--oa-fg-4)] hover:bg-[var(--oa-surface-2)] hover:text-[var(--oa-fg)]'
                                onClick={(e) => e.stopPropagation()}
                            >
                                <MoreHorizontal className='h-3.5 w-3.5' />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent side='bottom' align='end' className='min-w-[10rem]' onClick={(e) => e.stopPropagation()}>
                            <DropdownMenuItem data-testid='chat-conversation-pin' onSelect={() => onTogglePin(conv)}>
                                {pinned ? <PinOff className='mr-2 h-3.5 w-3.5' /> : <Pin className='mr-2 h-3.5 w-3.5' />}
                                {pinned ? t('chatBotPage.unpinConversation') : t('chatBotPage.pinConversation')}
                            </DropdownMenuItem>
                            {showDelete && (
                                <DropdownMenuItem data-testid='chat-conversation-delete' className='text-destructive focus:text-destructive' onSelect={() => onDelete(conv)}>
                                    <Trash2 className='mr-2 h-3.5 w-3.5' />
                                    {t('global.delete')}
                                </DropdownMenuItem>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>
        </motion.div>
        </div>
    );
});

interface Properties {
    selectedResource?: any;
    resourceSelected?: (resource: any) => void;
    children?: React.ReactNode;
}

/** Compare Mongo/API ids whether they are strings or ObjectId-like objects. */
function conversationIdsEqual(a: unknown, b: unknown): boolean {
    if (a == null || b == null) {
        return a === b;
    }
    return String(a) === String(b);
}

/** Patch per-instance synopsis fields on a conversation's worklist tasks. */
function patchInstanceSynopsisInConversation(
    conv: any,
    instanceId: string,
    event: { text: string; updatedAt?: any; source?: any }
): any {
    const worklist: any[] = conv?.worklist ?? [];
    let didPatch = false;
    const updatedWorklist = worklist.map((t: any) => {
        if (!conversationIdsEqual(t?.instanceId, instanceId)) return t;
        didPatch = true;
        return {
            ...t,
            instanceSynopsis: event.text,
            instanceSynopsisUpdatedAt: event.updatedAt,
            instanceSynopsisSource: event.source,
        };
    });
    if (!didPatch) return conv;
    return { ...conv, worklist: updatedWorklist };
}

/** Keep array identity stable when no conversation was patched. */
function patchInstanceSynopsisInConversations(
    conversations: any[],
    instanceId: string,
    event: { text: string; updatedAt?: any; source?: any }
): any[] {
    let didPatchAny = false;
    const updatedConversations = conversations.map((conv: any) => {
        const patched = patchInstanceSynopsisInConversation(conv, instanceId, event);
        if (patched !== conv) {
            didPatchAny = true;
        }
        return patched;
    });
    return didPatchAny ? updatedConversations : conversations;
}

export function ChatBot(properties: Properties) {
    const { t } = useTranslation();
    // Ziel der Konversationsliste: im AppShell der Slot in der Chat-Sidebar,
    // sonst `undefined` — dann bleibt die Liste als eigene Spalte hier.
    const chatSidebarSlot = useChatSidebarSlot();
    const inShell = chatSidebarSlot !== undefined;
    const [searchParams, setSearchParams] = useSearchParams();
    const [authentication] = useAtom(authenticationAtom) as any;
    const [hideDialog, setHideDialog] = useState(true);
    const [skillAgentResponse, _setSkillAgentResponse] = useState() as any;
    const [, _setInputFocussed] = useState(false);
    const [filterText, setFilterText] = useState('');
    const [conversations, setConversations] = useState([]) as any;
    // Erst nach der ersten Listen-Antwort (auch einer fehlgeschlagenen) weicht
    // das Lade-Skelett der Seitenleiste den echten Abschnitten.
    const [conversationsLoaded, setConversationsLoaded] = useState(false);
    const listReduceMotion = useReducedMotion() ?? false;
    // Paged list (as in the app): how many rows the server has handed us so far — the offset of
    // the next page and the window a refresh re-fetches — and whether the last page was full.
    const loadedConversationCountRef = useRef(0);
    const [hasMoreConversations, setHasMoreConversations] = useState(false);
    const [loadingMoreConversations, setLoadingMoreConversations] = useState(false);
    // Zustand der Shell-Sidebar (falls vorhanden): in der Symbolleiste
    // schrumpfen „Neuer Chat"/„Suchen" auf ihre Icons (mit Tooltip) und die
    // Konversationsliste blendet aus.
    const animatedSidebar = useAnimatedSidebarOptional();
    const railCollapsed = !!animatedSidebar && !animatedSidebar.open && !animatedSidebar.isMobile;
    // „Suchen" klappt das Filterfeld auf; beim Öffnen bekommt es den Fokus.
    const [searchOpen, setSearchOpen] = useState(false);
    const filterInputRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
        if (searchOpen) filterInputRef.current?.focus();
    }, [searchOpen]);
    useEffect(() => {
        if (railCollapsed && searchOpen) {
            setSearchOpen(false);
            setFilterText('');
        }
    }, [railCollapsed, searchOpen]);
    const [conversation, setConversation] = useState(null) as any;
    const [inputFocusTrigger, setInputFocusTrigger] = useState(0);
    const [scrollToEndTrigger, setScrollToEndTrigger] = useState(0);
    const [customerTools, setCustomerTools] = useState<CustomerToolOption[]>([]);
    const [customerToolsLoading, setCustomerToolsLoading] = useState(false);
    const [selectedToolId, setSelectedToolId] = useState('');
    // Conversations the agent is currently working on. Their progress dots live
    // in their own conversation, so the sidebar is what shows them elsewhere.
    const [runningConversationIds, setRunningConversationIds] = useState<string[]>([]);
    const runningConversations = useMemo(() => new Set(runningConversationIds), [runningConversationIds]);
    const conversationRef = useRef<any>(null);
    conversationRef.current = conversation;
    // Die „aktiv"-Pille der Kopfzeile meint *diese* Unterhaltung, nicht
    // irgendeine: Ein Lauf im Hintergrund gehört in die Seitenleiste. Die
    // Kopfzeile sitzt im AppShell über dem Chat, deshalb der kleine Kanal in
    // `chatRuns` statt eines durchgereichten Kontexts.
    const displayedConversationRunning =
        conversation?._id != null && runningConversations.has(String(conversation._id));
    useEffect(() => {
        publishDisplayedConversationRun(displayedConversationRunning);
    }, [displayedConversationRunning]);
    // Beim Verlassen der Chat-Seite bleibt sonst eine Pille auf einer Kopfzeile
    // stehen, die es nicht mehr gibt.
    useEffect(() => () => publishDisplayedConversationRun(false), []);
    /**
     * The conversation column is capped (`.chatColumn`, max-width 1400px) inside a much wider
     * `.chatContent`, so on a wide screen the gutters beside it are dead: the wheel lands on a
     * non-scrolling element and nothing moves. Forward those wheels to the conversation scroller,
     * so the whole area scrolls the chat rather than only the text column.
     */
    const chatContentRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        const host = chatContentRef.current;
        if (!host) return;

        /** Nearest ancestor that can actually take the scroll — so we never hijack a real scroller. */
        const findScrollableAt = (node: Node | null, deltaY: number): HTMLElement | null => {
            let el = node instanceof HTMLElement ? node : node?.parentElement ?? null;
            while (el && el !== host) {
                const style = getComputedStyle(el);
                const scrollable = /(auto|scroll)/.test(style.overflowY) &&
                    el.scrollHeight > el.clientHeight;
                const room = deltaY < 0
                    ? el.scrollTop > 0
                    : el.scrollTop + el.clientHeight < el.scrollHeight - 1;
                if (scrollable && room) return el;
                el = el.parentElement;
            }
            return null;
        };

        // A mouse wheel sends coarse notches (~100px); applied as-is they jump. They are
        // collected into a target the view glides to, like a native wheel scroll. A trackpad
        // already sends fine, momentum-smoothed deltas and is applied directly.
        const GLIDE_TAU_MS = 70;
        const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
        let target: number | null = null;
        let frame: number | undefined;
        let last = 0;
        let written: number | null = null;

        const stopGlide = () => {
            if (frame) cancelAnimationFrame(frame);
            frame = undefined;
            target = null;
            written = null;
        };

        const glide = (scroller: HTMLElement) => (now: number) => {
            if (target === null) return;
            // Someone else moved the view since our last step (the reader scrolling in the
            // text, the conversation following new output): stop instead of dragging it back.
            if (written !== null && Math.abs(scroller.scrollTop - written) > 1) {
                stopGlide();
                return;
            }
            const dt = Math.min(now - last, 64);
            last = now;
            const gap = target - scroller.scrollTop;
            if (Math.abs(gap) <= 0.5) {
                scroller.scrollTop = target;
                stopGlide();
                return;
            }
            scroller.scrollTop += gap * (1 - Math.exp(-dt / GLIDE_TAU_MS));
            written = scroller.scrollTop;
            frame = requestAnimationFrame(glide(scroller));
        };

        const onWheel = (e: WheelEvent) => {
            if (e.ctrlKey || e.metaKey) return; // zoom gestures are not ours to redirect
            if (findScrollableAt(e.target as Node, e.deltaY)) {
                stopGlide(); // something else handles it — and a running glide must not pull back
                return;
            }

            const scroller = host.querySelector('.messages') as HTMLElement | null;
            if (!scroller || scroller.scrollHeight <= scroller.clientHeight) return;

            // deltaMode: 0 = pixels, 1 = lines, 2 = pages
            const step = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? scroller.clientHeight : 1;
            const delta = e.deltaY * step;

            const trackpad = e.deltaMode === 0 && Math.abs(e.deltaY) < 40;
            if (trackpad || reduceMotion) {
                stopGlide();
                scroller.scrollTop += delta;
                return;
            }

            const max = scroller.scrollHeight - scroller.clientHeight;
            target = Math.max(0, Math.min(max, (target ?? scroller.scrollTop) + delta));
            if (!frame) {
                last = performance.now();
                frame = requestAnimationFrame(glide(scroller));
            }
        };

        // Passive on purpose: this listener sits on an ancestor of the conversation scroller,
        // and a non-passive one makes the browser wait for the main thread before *every*
        // wheel scroll in the chat — while an answer streams in, that thread is busy rendering
        // markdown and scrolling hangs, then catches up with a jump. Nothing in the gutters
        // scrolls natively, so there is no default to prevent.
        host.addEventListener('wheel', onWheel, { passive: true });
        return () => {
            host.removeEventListener('wheel', onWheel);
            stopGlide();
        };
    }, []);
    /**
     * Register the chat surface with the morph module (`src/guide/morph/`). When
     * the user clicks a guided link here, `useGuideNavigate` measures and clones
     * exactly this surface in the click handler — that becomes the shell that
     * flies to the question panel's place.
     *
     * The surface registers **itself** instead of the navigator looking it up via
     * `document.querySelector`: otherwise there would be two truths about what
     * "the chat surface" is. Only inside the shell — without the shell there is
     * no panel to morph into.
     */
    useEffect(() => {
        const node = chatContentRef.current;
        if (!inShell || !node) return;
        return registerAssistantSurface({
            node,
            column: () => node.querySelector<HTMLElement>('.chatColumn'),
            scroller: () => node.querySelector<HTMLElement>('.messages'),
            conversation: () => conversationRef.current,
        });
    }, [inShell]);
    /** Prozesse rows in rendered order; filled from the render below. */
    const orderedProcessConversationsRef = useRef<any[]>([]);
    const conversationsRef = useRef<any[]>(conversations);
    conversationsRef.current = conversations;
    const searchParamsRef = useRef(searchParams);
    searchParamsRef.current = searchParams;
    /**
     * react-router re-creates setSearchParams on every URL change. Using it
     * directly in a useCallback dependency list would re-create that callback
     * per conversation switch — and re-run any effect keyed on it, which is
     * exactly how the conversation list ended up being re-fetched each time.
     * This wrapper has a stable identity; call it instead of setSearchParams.
     */
    const setSearchParamsRef = useRef(setSearchParams);
    setSearchParamsRef.current = setSearchParams;
    const updateSearchParams: typeof setSearchParams = useCallback(
        (...args: Parameters<typeof setSearchParams>) => setSearchParamsRef.current(...args),
        [],
    );
    /**
     * Id of the conversation that is open or currently being fetched. Every path
     * that swaps the open conversation stamps it here before it starts loading,
     * so the URL effect below can tell "the user just switched and the body is
     * already on its way" from "the URL changed on its own (back/forward, deep
     * link)" and only fetches in the latter case.
     */
    const loadedConversationIdRef = useRef<string | null>(searchParams.get('conversationId'));

    const toggleHideDialog = useCallback(() => {
        setHideDialog(!hideDialog);
    }, [hideDialog]);

    const clientActionTitle = (skillAgentResponse: any) => {
        if (!skillAgentResponse || !skillAgentResponse.skill) {
            return 'Client Action';
        }

        switch (skillAgentResponse.skill.id) {
            case 'clientAction/wayfinding': return 'Wayfinding';
            case 'clientAction/showDetails': return 'Resource Details';
            default: return 'Client Action';
        }
    }

    const clearToolSelection = useCallback(() => {
        setSelectedToolId('');
    }, []);

    const handleToolChange = useCallback((toolId: string) => {
        setSelectedToolId(toolId);
    }, []);

    useEffect(() => {
        if (!authentication?.customer) return;

        let cancelled = false;
        const loadTools = async () => {
            setCustomerToolsLoading(true);
            try {
                const toolsData = await userAPI.getCustomerTools(authentication.customer);
                if (!cancelled) {
                    setCustomerTools(mapCustomerToolsForChat(toolsData));
                }
            } catch (error) {
                console.error('Error loading customer tools:', error);
                if (!cancelled) {
                    setCustomerTools([]);
                }
            } finally {
                if (!cancelled) {
                    setCustomerToolsLoading(false);
                }
            }
        };

        loadTools();
        return () => {
            cancelled = true;
        };
    }, [authentication?.customer]);

    const createConversation = async () => {
        clearToolSelection();
        try {
            const newConversation = await userAPI.createConversation(authentication.customer);

            setConversation(newConversation);
            setConversations([...conversations, newConversation]);
            
            // Update URL with new conversation ID
            if (newConversation._id) {
                loadedConversationIdRef.current = String(newConversation._id);
                updateSearchParams({ conversationId: newConversation._id });
            }
            setInputFocusTrigger((t) => t + 1);
        } catch (error) {
            console.error('Error creating conversation:', error);
            // The global error handler will handle authentication errors
        }
    }

    const getTaskDisplayName = (task: any) =>
        task?.name ?? task?.label ?? task?.taskName ?? task?.taskDefinitionName ?? '';

    /** Maps nodeId → tri:userPrompt, populated from conversations/list response (survives Conversation model stripping) */
    const userPromptMapRef = useRef<Record<string, string>>({});
    /** Maps nodeId → all `tri:*` attrs (keys without prefix). Generic carrier for tri:userPrompt, tri:uxType, etc. */
    const triAttrsByNodeIdRef = useRef<Record<string, Record<string, string>>>({});

    const applyActiveTask = useCallback((conv: any, task: any) => {
        const taskName = getTaskDisplayName(task) || 'Task';
        const triAttrs = getTaskTriAttrs(task, {
            triAttrsByNodeId: {
                ...triAttrsByNodeIdRef.current,
                ...((conv as any)?.triAttrsByNodeId ?? {}),
            },
        });
        const userPrompt = triAttrs?.userPrompt
            ?? task?.userPrompt
            ?? (conv as any)?.userPromptMap?.[task?.nodeId]
            ?? userPromptMapRef.current[task?.nodeId];
        const activeTask = (triAttrs || userPrompt)
            ? { ...task, ...(triAttrs ? { triAttrs } : {}), ...(userPrompt ? { userPrompt } : {}) }
            : task;
        const updated = { ...conv, activeTask };
        const phraseIndex = Math.floor(Math.random() * 10) + 1;
        const content = userPrompt
            ? userPrompt
            : t(`chatBotPage.taskActivated${phraseIndex}`, { taskName });
        const msg = new Message({
            type: MessageType.botMessage,
            content,
            confirmation: false,
            resources: [],
        });
        (msg as any)._taskActivation = true;
        let messages = [...(conv.messages || [])];
        if (messages.length > 0) {
            const last = messages[messages.length - 1] as any;
            if (last._taskActivation) {
                messages = messages.slice(0, -1);
            }
        }
        updated.messages = [...messages, msg];
        return updated;
    }, [t]);

    /**
     * Load one conversation body and make it the open one. Switching costs
     * exactly this one GET — the sidebar list and the theme are untouched.
     * `listConv` is the (lightweight) row the user clicked, used as worklist
     * fallback when the server response carries none.
     */
    const loadConversation = useCallback(async (id: string, listConv?: any, signal?: AbortSignal): Promise<boolean> => {
        loadedConversationIdRef.current = String(id);
        try {
            const full = await userAPI.getConversation(authentication.customer, id, signal);
            // A newer switch started while this request was in flight — its
            // response must win, so drop this one instead of rendering it.
            if (!conversationIdsEqual(loadedConversationIdRef.current, id)) return false;
            const worklistFromServer = (full as any).worklist;
            const worklistFromList = listConv?.worklist;
            (full as any).worklist = (worklistFromServer?.length ? worklistFromServer : worklistFromList) ?? [];
            const worklist = (full as any).worklist;
            if (worklist?.length === 1) {
                const updated = applyActiveTask(full, worklist[0]);
                setConversation(updated);
                setConversations((prev: any[]) => prev.map((c: any) => String(c._id) === String(updated._id) ? { ...c, worklist: listConv?.worklist ?? c.worklist } : c));
                setScrollToEndTrigger((t) => t + 1);
            } else if (worklist?.length > 1) {
                const updated = applyActiveTask(full, worklist[0]);
                setConversation(updated);
                setConversations((prev: any[]) => prev.map((c: any) => String(c._id) === String(updated._id) ? updated : c));
                setScrollToEndTrigger((t) => t + 1);
            } else {
                setConversation(full);
            }
            return true;
        } catch (e) {
            if (e instanceof DOMException && e.name === 'AbortError') return false;
            console.error('Error loading conversation:', e);
            return false;
        }
    }, [authentication.customer, applyActiveTask]);

    /** A turn that ran without this tab's stream has finished: show its result (ChatExchange). */
    const reloadConversation = useCallback((id: string) => loadConversation(id), [loadConversation]);

    const selectConversation = useCallback(async (conv: any) => {
        const id = conv._id;
        if (!id) {
            setConversation(conv);
            return;
        }
        if (await loadConversation(id, conv)) {
            updateSearchParams({ conversationId: id }, { replace: true });
        }
    }, [loadConversation, updateSearchParams]);

    const handleWorklistTaskSelect = useCallback((task: any) => {
        const conv = conversation;
        if (!conv?.worklist?.length) return;
        const updated = applyActiveTask(conv, task);
        setConversation(updated);
        setConversations((prev: any[]) => prev.map((c: any) => c._id === updated._id ? updated : c));
        setInputFocusTrigger((t) => t + 1);
        setScrollToEndTrigger((t) => t + 1);
        if (updated._id) {
            updateSearchParams({ conversationId: updated._id }, { replace: true });
        }
    }, [conversation, applyActiveTask, updateSearchParams]);

    const updateConversation = async (conversation: Conversation) => {
        try {
            // A conversation may stream while the user reads another one, so an
            // update only opens a conversation that is already open — otherwise
            // a finishing background run would yank the view away from what the
            // user is reading. The sidebar list is updated either way.
            const isOpenConversation = String((conversation as any)._id) === String(conversationRef.current?._id);
            if (isOpenConversation) {
                // Use the provided conversation object directly instead of fetching all conversations
                setConversation(conversation);
            }

            // Update the conversations list to include the updated conversation
            setConversations((prevConversations: any[]) => {
                const updatedConversations = prevConversations.map((c: any) =>
                    c._id === conversation._id ? conversation : c
                );
                return updatedConversations;
            });

            // After task completion, if worklist has tasks, activate the first one
            const worklist = (conversation as any).worklist;
            if (worklist?.length >= 1) {
                const updated = applyActiveTask(conversation, worklist[0]);
                if (isOpenConversation) {
                    setConversation(updated);
                    setScrollToEndTrigger((t) => t + 1);
                }
                setConversations((prevConversations: any[]) =>
                    prevConversations.map((c: any) => c._id === updated._id ? updated : c)
                );
            }
        } catch (error) {
            console.error('Error updating conversation:', error);
            // The global error handler will handle authentication errors
        }
    }

    const onCompleteTaskClosed = useCallback(async () => {
        try {
            const currentConv = conversation;
            if (!currentConv?._id) return;
            const newConversations = conversations.filter((c: any) => c._id !== currentConv._id);
            clearToolSelection();

            // Keep the user in their work queue: open the row that moves up into
            // the finished conversation's place instead of a fresh, empty one.
            // The order is the sidebar's own (see orderedProcessConversationsRef).
            const nextConversation = pickNextConversation(
                orderedProcessConversationsRef.current,
                currentConv._id
            );
            if (nextConversation?._id) {
                setConversations(newConversations);
                await selectConversation(nextConversation);
                setInputFocusTrigger((t) => t + 1);
                return;
            }

            // Nothing left to work on — start a new conversation, as before.
            const newConversation = await userAPI.createConversation(authentication.customer);
            setConversations([...newConversations, newConversation]);
            setConversation(newConversation);
            if (newConversation._id) {
                loadedConversationIdRef.current = String(newConversation._id);
                updateSearchParams({ conversationId: newConversation._id });
            }
            setInputFocusTrigger((t) => t + 1);
        } catch (error) {
            console.error('Error closing conversation and creating new:', error);
        }
    }, [conversation, conversations, authentication.customer, clearToolSelection, selectConversation, updateSearchParams]);

    const deleteConversation = useCallback(async (deletedConversation: any) => {
        try {
            const currentConv = conversationRef.current;
            const newConversations = conversations.filter((c: any) => c._id !== deletedConversation._id);

            setConversations(newConversations);

            if (deletedConversation._id === currentConv?._id) {
                // If we're deleting the currently selected conversation, create a new one
                clearToolSelection();
                const newConversation = await userAPI.createConversation(authentication.customer);
                setConversation(newConversation);
                setConversations([...newConversations, newConversation]);
                // Update URL with new conversation ID
                if (newConversation._id) {
                    loadedConversationIdRef.current = String(newConversation._id);
                    updateSearchParams({ conversationId: newConversation._id });
                }
                setInputFocusTrigger((t) => t + 1);
            } else if (newConversations.length > 0) {
                // If there are other conversations, select the first one
                const firstConversation = newConversations[0];
                if (firstConversation._id) {
                    loadedConversationIdRef.current = String(firstConversation._id);
                    userAPI.getConversation(authentication.customer, firstConversation._id).then((full) => {
                        const worklistFromServer = (full as any).worklist;
                        const worklistFromList = firstConversation.worklist;
                        (full as any).worklist = (worklistFromServer?.length ? worklistFromServer : worklistFromList) ?? [];
                        const worklist = (full as any).worklist;
                        const toSet = worklist?.length ? applyActiveTask(full, worklist[0]) : full;
                        setConversation(toSet);
                        updateSearchParams({ conversationId: firstConversation._id });
                    }).catch(() => {
                        setConversation(firstConversation);
                        updateSearchParams({ conversationId: firstConversation._id });
                    });
                } else {
                    setConversation(firstConversation);
                }
            } else {
                // No conversations left, clear URL parameter
                updateSearchParams({});
            }

            await userAPI.deleteConversation(authentication.customer, deletedConversation);
        } catch (error) {
            console.error('Error deleting conversation:', error);
        }
    }, [conversations, authentication.customer, applyActiveTask, clearToolSelection, updateSearchParams]);

    const filterConversationsByContent = useCallback((conversations: any[], filterText: string) => {
        if (!filterText) return conversations;
        return conversations.filter((conversation: any) =>
            conversation.messages?.some((message: any) =>
                message.content && message.content.toLowerCase().includes(filterText.toLowerCase())
            )
        );
    }, []);

    const refreshConversations = useCallback(async (signal?: AbortSignal, options?: { silent?: boolean; skipFullFetchForCurrent?: boolean; ignoreUrlConversationId?: boolean }) => {
        const silent = options?.silent ?? false;
        const skipFullFetchForCurrent = options?.skipFullFetchForCurrent ?? false;
        try {
            const page = refreshPage(loadedConversationCountRef.current);
            const newConversations = await userAPI.getConversationList(authentication.customer, signal, page);
            loadedConversationCountRef.current = newConversations.length;
            setHasMoreConversations(pageHasMore(newConversations, page));

            // Extract userPromptMap and triAttrsByNodeId from raw list data
            // (before the Conversation model strips unknown properties).
            for (const c of newConversations) {
                if (c.userPromptMap) {
                    Object.assign(userPromptMapRef.current, c.userPromptMap);
                }
                if ((c as any).triAttrsByNodeId) {
                    Object.assign(triAttrsByNodeIdRef.current, (c as any).triAttrsByNodeId);
                }
            }

            const conversationIdFromUrl = options?.ignoreUrlConversationId
                ? null
                : searchParamsRef.current.get('conversationId');
            const currentConv = conversationRef.current;

            // Find the conversation to select: prefer URL, then current, then first in list
            let selectedConversation: any = null;
            let mergedConversations = newConversations;

            if (conversationIdFromUrl) {
                selectedConversation = newConversations.find((c: any) =>
                    conversationIdsEqual(c._id, conversationIdFromUrl),
                );
                if (!selectedConversation && conversationIdsEqual(currentConv?._id, conversationIdFromUrl)) {
                    // URL points to current conversation but it's not in API yet (e.g. just created).
                    // Preserve current to avoid overwriting with old content.
                    selectedConversation = currentConv;
                    const alreadyInList = newConversations.some((c: any) =>
                        conversationIdsEqual(c._id, currentConv._id),
                    );
                    if (!alreadyInList) {
                        mergedConversations = [currentConv, ...newConversations];
                    }
                }
            }

            if (!selectedConversation && !conversationIdFromUrl) {
                const updated = newConversations.find((c: any) =>
                    conversationIdsEqual(c._id, currentConv?._id),
                );
                selectedConversation = updated ?? currentConv;
            }

            let createdInThisRefresh = false;
            if (!selectedConversation && !conversationIdFromUrl) {
                if (newConversations.length === 0 && !silent) {
                    clearToolSelection();
                    const created = await userAPI.createConversation(authentication.customer);
                    if (created._id) {
                        loadedConversationIdRef.current = String(created._id);
                        updateSearchParams({ conversationId: created._id });
                    }
                    selectedConversation = created;
                    mergedConversations = [created];
                    createdInThisRefresh = true;
                } else if (newConversations.length > 0) {
                    selectedConversation = newConversations[0];
                    if (!conversationIdFromUrl && selectedConversation._id && !silent) {
                        updateSearchParams({ conversationId: selectedConversation._id });
                    }
                }
            }

            const fallbackConversation = selectedConversation
                ?? newConversations.find((c: any) => conversationIdsEqual(c._id, currentConv?._id))
                ?? currentConv
                ?? newConversations[0]
                ?? null;

            setConversations(mergedConversations);
            setConversationsLoaded(true);

            let idToFetch: string | null = null;
            if (!createdInThisRefresh) {
                if (selectedConversation?._id != null && String(selectedConversation._id) !== '') {
                    idToFetch = String(selectedConversation._id);
                } else if (conversationIdFromUrl) {
                    idToFetch = conversationIdFromUrl;
                }
            }

            if (idToFetch || selectedConversation) {
                const id = idToFetch ?? selectedConversation?._id;
                const isCurrentConversation = conversationIdsEqual(currentConv?._id, id);
                if (id && !createdInThisRefresh && !(skipFullFetchForCurrent && isCurrentConversation)) {
                    try {
                        loadedConversationIdRef.current = String(id);
                        const full = await userAPI.getConversation(authentication.customer, id, signal);
                        // The user switched conversations while this background
                        // refresh was in flight — do not clobber their choice.
                        if (!conversationIdsEqual(loadedConversationIdRef.current, id)) return;
                        const worklistFromServer = (full as any).worklist;
                        const worklistFromList = selectedConversation?.worklist;
                        (full as any).worklist = (worklistFromServer?.length ? worklistFromServer : worklistFromList) ?? [];
                        const worklist = (full as any).worklist;
                        if (conversationIdsEqual(currentConv?._id, (full as any)._id) && (currentConv as any).activeTask) {
                            const taskActivationMessages = (currentConv.messages || []).filter((m: any) => (m as any)._taskActivation);
                            if (taskActivationMessages.length > 0) {
                                (full as any).activeTask = (currentConv as any).activeTask;
                                (full as any).messages = [...((full as any).messages || []), ...taskActivationMessages];
                            }
                        }
                        const toSet = worklist?.length && !(full as any).activeTask
                            ? applyActiveTask(full, worklist[0])
                            : full;
                        // Don't overwrite with stale data: use FRESH ref (another refresh may have updated since we started)
                        const nowCurrent = conversationRef.current;
                        const nowMsgCount = (nowCurrent?.messages?.length ?? 0);
                        const fetchedMsgCount = ((toSet as any).messages?.length ?? 0);
                        if (conversationIdsEqual(nowCurrent?._id, (toSet as any)._id) && nowMsgCount > fetchedMsgCount) {
                            return;
                        }
                        setConversation(toSet);
                        if (worklist?.length && !(full as any).activeTask) {
                            setScrollToEndTrigger((t) => t + 1);
                        }
                    } catch (e) {
                        if (e instanceof DOMException && e.name === 'AbortError') {
                            return;
                        }
                        const statusCode = typeof (e as { status?: unknown })?.status === 'number'
                            ? (e as { status: number }).status
                            : null;
                        const accessDenied = statusCode === 403 || statusCode === 404;
                        if (
                            accessDenied &&
                            conversationIdFromUrl &&
                            !selectedConversation
                        ) {
                            // Direct-by-URL fetch failed (e.g. not list-visible or forbidden). Drop param so we do not keep the wrong URL / wrong selection.
                            if (!silent) {
                                console.warn(
                                    '[Chat] Conversation not accessible by direct id; clearing conversationId from URL.',
                                    conversationIdFromUrl,
                                );
                            }
                            updateSearchParams((prev) => {
                                const next = new URLSearchParams(prev);
                                next.delete('conversationId');
                                return next;
                            });
                            // Re-resolve the selection without the bad id (pick the
                            // first conversation, or create one). This used to happen
                            // by way of the URL change re-running the mount effect,
                            // which no longer reacts to searchParams. Terminates after
                            // one extra pass: the retry ignores the URL id, so it
                            // cannot land in this branch again.
                            await refreshConversationsRef.current?.(signal, {
                                ...options,
                                ignoreUrlConversationId: true,
                            });
                            return;
                        }
                        // Don't overwrite with list data (has $slice:1 = 1 msg) when current has more
                        const fallbackSelection = selectedConversation ?? fallbackConversation;
                        const currentMsgCount = (currentConv?.messages?.length ?? 0);
                        const listMsgCount = (fallbackSelection?.messages?.length ?? 0);
                        if (
                            fallbackSelection &&
                            conversationIdsEqual(currentConv?._id, fallbackSelection?._id) &&
                            currentMsgCount > listMsgCount
                        ) {
                            return;
                        }
                        if (fallbackSelection) {
                            setConversation(fallbackSelection);
                        }
                    }
                } else if (!(skipFullFetchForCurrent && isCurrentConversation) && selectedConversation) {
                    setConversation(selectedConversation);
                }
            }
        } catch (error) {
            if (error instanceof DOMException && error.name === 'AbortError') {
                return;
            }
            // Auch im Fehlerfall gilt der erste Versuch als beantwortet —
            // sonst stünde das Skelett dauerhaft.
            setConversationsLoaded(true);
            if (!silent) {
                console.error('Error loading conversations:', error);
            }
        }
    }, [authentication.customer, applyActiveTask, clearToolSelection, updateSearchParams]);

    // Lets refreshConversations re-enter itself once (see the access-denied
    // branch) without taking a dependency on its own identity.
    const refreshConversationsRef = useRef(refreshConversations);
    refreshConversationsRef.current = refreshConversations;

    /** Appends the next (older) page when the list is scrolled to its end. */
    const loadMoreConversations = useCallback(async () => {
        if (loadingMoreConversations || !hasMoreConversations) return;
        setLoadingMoreConversations(true);
        try {
            const page = nextPage(loadedConversationCountRef.current);
            const rows = await userAPI.getConversationList(authentication.customer, undefined, page);
            loadedConversationCountRef.current += rows.length;
            setHasMoreConversations(pageHasMore(rows, page));
            for (const c of rows) {
                if (c.userPromptMap) Object.assign(userPromptMapRef.current, c.userPromptMap);
                if ((c as any).triAttrsByNodeId) Object.assign(triAttrsByNodeIdRef.current, (c as any).triAttrsByNodeId);
            }
            setConversations((previous: any[]) => appendPage(previous, rows));
        } catch (error) {
            console.error('Error loading more conversations:', error);
        } finally {
            setLoadingMoreConversations(false);
        }
    }, [authentication.customer, hasMoreConversations, loadingMoreConversations]);

    const conversationIdFromUrl = searchParams.get('conversationId');

    // Initial load only: fetch the sidebar list once and resolve which
    // conversation to open. Deliberately not keyed on searchParams — switching
    // conversations must not re-fetch the (expensive) list. It stays fresh via
    // the 60s timer, the post-stream refresh and the websocket patches below.
    // Runs via the ref and an empty dependency list: the callback's identity
    // changes on most renders (it closes over per-render helpers), and under
    // the AppShell this component renders often — keyed on the identity this
    // "initial" effect fired continuously, each run re-loading the URL
    // conversation and stealing loadedConversationIdRef from a click.
    useEffect(() => {
        const abortController = new AbortController();
        refreshConversationsRef.current(abortController.signal);
        return () => abortController.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Conversation changed from outside the UI (browser back/forward, deep
    // link). Row clicks and the create/delete paths already loaded the
    // conversation and stamped loadedConversationIdRef, so for them this is a
    // no-op — that is what keeps a switch at a single GET.
    useEffect(() => {
        if (!conversationIdFromUrl) return;
        if (conversationIdsEqual(loadedConversationIdRef.current, conversationIdFromUrl)) return;
        const abortController = new AbortController();
        const listConv = conversationsRef.current.find((c: any) =>
            conversationIdsEqual(c._id, conversationIdFromUrl),
        );
        loadConversation(conversationIdFromUrl, listConv, abortController.signal);
        return () => abortController.abort();
    }, [conversationIdFromUrl, loadConversation]);

    // Same ref-based wiring as the initial load: one stable 60s interval
    // instead of tearing it down on every identity change of the callback.
    useEffect(() => {
        const intervalId = setInterval(() => {
            refreshConversationsRef.current(undefined, { silent: true });
        }, 60 * 1000);
        return () => clearInterval(intervalId);
    }, []);

    // Subscribe to instance-synopsis updates so worklist rows reflect new
    // labels without waiting for the next 60s refresh. tri-server emits the
    // event after the post-task-completion hook writes a new synopsis.
    useEffect(() => {
        const socket = io(configuration.serverUrl);
        socket.on('instance-synopsis-updated', (event: any) => {
            const eventCustomer = event?.customerId ?? event?.customer;
            if (eventCustomer && !conversationIdsEqual(eventCustomer, authentication?.customer)) return;
            const eventUser = event?.userEmail ?? event?.email ?? event?.user;
            if (
                eventUser &&
                authentication?.email &&
                String(eventUser).toLowerCase() !== String(authentication.email).toLowerCase()
            ) {
                return;
            }
            const instanceId: string | undefined = event?.instanceId;
            const text: string | undefined = event?.text;
            if (!instanceId || typeof text !== 'string') return;
            const synopsisEvent = {
                text,
                updatedAt: event.updatedAt,
                source: event.source,
            };
            setConversations((prev: any[]) => patchInstanceSynopsisInConversations(prev, instanceId, synopsisEvent));
            setConversation((prev: any) => patchInstanceSynopsisInConversation(prev, instanceId, synopsisEvent));
        });
        return () => {
            socket.disconnect();
        };
    }, [authentication?.customer, authentication?.email]);

    /**
     * Zeitpunkt, nach dem eine Zeile einsortiert wird und dessen Alter sie
     * rechts anzeigt: der Beginn der Konversation. Eine Prozesszeile ohne
     * Nachricht ist gerade erst entstanden und zählt als „jetzt"; eine
     * gewöhnliche Konversation ohne Nachricht hat nichts zu zeigen und bleibt
     * aus der Liste (das war schon die Wirkung der früheren Datums-Filter).
     */
    const conversationTimestamp = useCallback((conv: any): number | undefined => {
        const date = conv.messages?.[0]?.date;
        if (date) {
            const time = new Date(date).getTime();
            return Number.isNaN(time) ? undefined : time;
        }
        return isProcessConversationForViewer(conv, authentication?.email) ? Date.now() : undefined;
    }, [authentication?.email]);

    const filteredConversations = useMemo(
        () => filterConversationsByContent(conversations, filterText),
        [conversations, filterConversationsByContent, filterText]
    );

    // Eine Liste, absteigend nach Alter. Die Datums-Trenner („Heute", „Die
    // letzten 30 Tage", …) sind entfallen — jede Zeile trägt ihr Alter selbst.
    const pinnedConversations = usePinnedConversations();
    const listedConversations = useMemo(
        () => orderConversations(
            filteredConversations.filter((c: any) => conversationTimestamp(c) !== undefined),
            pinnedConversations,
            (c: any) => c._id,
            conversationTimestamp,
        ),
        [filteredConversations, conversationTimestamp, pinnedConversations]
    );

    const processConversations = useMemo(
        () => listedConversations.filter((c: any) => isProcessConversationForViewer(c, authentication?.email) && !isEndedProcessConversation(c)),
        [listedConversations, authentication?.email]
    );
    // Processes that ended in one of the viewer's lanes — a section of their own.
    const endedProcessConversations = useMemo(
        () => listedConversations.filter((c: any) => isEndedProcessConversation(c)),
        [listedConversations]
    );
    // The Prozesse rows in the order they are rendered, for handlers that run
    // long after render (finishing a task) and must follow what the user sees.
    // A ref, because those handlers are declared above these memos.
    orderedProcessConversationsRef.current = processConversations;
    const otherConversations = useMemo(
        () => listedConversations.filter((c: any) => !isProcessConversationForViewer(c, authentication?.email) && !isEndedProcessConversation(c)),
        [listedConversations, authentication?.email]
    );

    // Sum of worklist entries (to-dos) across all process conversations shown.
    const processTodoCount = useMemo(
        () => processConversations.reduce((n: number, c: any) => n + (c.worklist?.length ?? 0), 0),
        [processConversations]
    );

    const togglePin = useCallback((conv: any) => { toggleConversationPinned(conv?._id); }, []);

    // Which conversations the list showed at the previous render: a row whose id is new unfolds
    // from the top (a fresh chat, a refresh that brought one in). `null` until the first list
    // stood, so the initial fill does not animate.
    const knownConversationIdsRef = useRef<Set<string> | null>(null);
    const knownIds = knownConversationIdsRef.current;
    useEffect(() => {
        knownConversationIdsRef.current = new Set(listedConversations.map((c: any) => String(c._id)));
    }, [listedConversations]);

    const renderConversationRow = (conv: any, index: number) => (
        <EnterRow animate={knownIds !== null && !knownIds.has(String(conv._id))}>
        <ConversationRow
            key={conv._id || index}
            conv={conv}
            isSelected={String(conv._id) === String(conversation?._id)}
            onSelect={selectConversation}
            onDelete={deleteConversation}
            title={conv.messages?.[0]?.content || t('ui.newConversation')}
            worklistCount={conv.worklist?.length ?? 0}
            hasWorklist={(conv.worklist?.length ?? 0) > 0}
            // activeTask wins when the user picked a specific task of a
            // multi-task worklist; otherwise it is the one applyActiveTask uses.
            currentNode={getTaskDisplayName(conv.activeTask ?? conv.worklist?.[0])}
            showDelete={
                isConversationOwner(conv, authentication?.email) || !conv.processInstance
            }
            viewerEmail={authentication?.email}
            isRunning={runningConversations.has(String(conv._id))}
            timestamp={conversationTimestamp(conv)}
            pinned={pinnedConversations.has(String(conv._id))}
            onTogglePin={togglePin}
        />
        </EnterRow>
    );

    /**
     * Ein Abschnitt je Kategorie: eine sehr kleine Überschrift, rechts daneben
     * der Zähler und — bei „Konversationen" — der Plus-Knopf, darunter die
     * Zeilen. Die Abschnitte teilen sich den Platz und scrollen jeder für sich.
     */
    const renderCategory = (heading: string, convs: any[], options?: { count?: number; action?: React.ReactNode; always?: boolean; fit?: boolean; onEndReached?: () => void; loadingMore?: boolean }) => (convs.length > 0 || options?.always
        ?
        // `fit`: der Abschnitt wächst nur mit seinem Inhalt (Prozesse sind
        // meist wenige) und ist bei der halben Listenhöhe gedeckelt — ab dort
        // scrollt er innen. Ohne `fit` nimmt der Abschnitt den Rest.
        <div className={cn('chat-conversation-section flex min-h-0 flex-col', options?.fit ? 'shrink grow-0 basis-auto max-h-[50%]' : 'flex-1')}>
            {/* `pr-3` matches the rows' `px-3`: the plus sits in a 24px cell on the same x as the
                "…" button at the end of each row. */}
            <div className='flex h-7 shrink-0 items-center gap-2 pl-2 pr-3'>
                <span className='text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground'>
                    {heading}
                </span>
                {options?.count != null && options.count > 0 && (
                    <span className='ml-auto text-[10px] text-[var(--oa-fg-4)]'>{options.count}</span>
                )}
                {options?.action && (
                    <span className={cn('flex w-6 shrink-0 items-center justify-center', (options?.count == null || options.count <= 0) && 'ml-auto')}>
                        {options.action}
                    </span>
                )}
            </div>
            <div className='oa-scroll min-h-0 flex-1 overflow-y-auto overflow-x-hidden'>
                {/* Wie die Settings-Navigation: eine Pille gleitet per
                    Shared-Layout-Animation unter der überfahrenen Zeile
                    entlang. `SharedLayoutBg` verlangt schlichte DOM-Kinder
                    (es tauscht deren children aus), daher der Div-Mantel um
                    jede Zeile. */}
                {/* The hover pill spans the whole entry, "…" included (user decision 28.09.2026);
                    only the selection tint stops at the row. */}
                <SharedLayoutBg as='div' inset={0} pillClassName='rounded-xl bg-muted/70' className='gap-0.5'>
                    {convs.map((conv: any, index: number) => (
                        <div key={conv._id || index}>{renderConversationRow(conv, index)}</div>
                    ))}
                </SharedLayoutBg>
                {/* Paged like the app: the sentinel at the end asks for the next page while
                    scrolling; a skeleton row stands in for the rows on their way. */}
                {options?.onEndReached && (
                    <LoadMoreSentinel active={!options.loadingMore} onVisible={options.onEndReached} />
                )}
                {options?.loadingMore && (
                    // Three ghost rows in the shape of the real ones (title bar, age stub), so
                    // the list visibly grows while the next page is on its way.
                    <div className='flex flex-col gap-0.5' data-testid='chat-conversations-loading-more' aria-busy='true'>
                        {[68, 52, 76].map((width) => (
                            <div key={width} className='flex min-h-9 items-center gap-2.5 px-3 py-1.5'>
                                <Skeleton className='h-3.5 rounded-md' style={{ width: `${width}%` }} />
                                <Skeleton className='ml-auto h-3 w-5 rounded-md' />
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
        :
        <></>
    );

    // Der frühere breite Knopf „Neue Konversation" ist auf ein Plus neben der
    // Überschrift „Konversationen" geschrumpft — dieselbe Aktion, nur ohne
    // eigene Zeile über der Liste.
    const newConversationButton = (
        <Button
            type="button"
            variant="ghost"
            size="icon"
            data-testid="chat-new-conversation"
            aria-label={t('ui.newConversationButton')}
            // Same cut as the "…" button at the end of each row: 24px ghost, no resting fill,
            // no native tooltip. `h-6 w-6` (not `size-*`) overrides the icon size's h/w, and the
            // `max-sm:` pair its 44px touch size — that one spilled down over the first row.
            className="h-6 w-6 max-sm:h-6 max-sm:w-6 rounded-md text-[var(--oa-fg-4)] hover:bg-[var(--oa-surface-2)] hover:text-[var(--oa-fg)]"
            disabled={conversation && conversation.messages.length === 0}
            onClick={() => createConversation()}
        >
            <Plus className="h-3.5 w-3.5" />
        </Button>
    );

    // Konversationsliste — in sich geschlossener Block (Filter, Abschnitte
    // „Prozesse"/„Konversationen" samt Lauf-Spinner, Plus-Knopf für eine neue
    // Konversation). Er kennt vom umgebenden Layout nichts außer seinem
    // eigenen Container und wird im AppShell-Betrieb per Portal in den
    // Sidebar-Slot gehängt; alle Zustände und Handler bleiben hier in ChatBot
    // (Concurrent-Runs-Pfad unberührt). Ohne Shell (Slot-Kontext `undefined`)
    // rendert er wie bisher als eigene Spalte im Chat.
    /* Label der Kopf-Zeilen („Neuer Chat"/„Suchen"): in der Symbolleiste
       blendet es aus, das Icon bleibt stehen. */
    const railLabelClass = cn(
        'min-w-0 flex-1 truncate transition-opacity duration-200',
        railCollapsed && 'opacity-0',
    );
    /* In der Symbolleiste bekommen die Kopf-Zeilen denselben Tooltip rechts
       wie die Menüzeilen der Settings-Seitenleiste. */
    const withRailTooltip = (control: React.ReactElement, label: string) =>
        railCollapsed ? (
            <Tooltip>
                <TooltipTrigger asChild>{control}</TooltipTrigger>
                <TooltipContent side="right" sideOffset={10} className="text-sm font-medium">
                    {label}
                </TooltipContent>
            </Tooltip>
        ) : (
            control
        );

    const conversationList = (
        <div className={cn(
            'flex flex-col gap-2',
            // Im Shell füllt der Block den Slot; ohne Shell bestimmt App.sass
            // die Spalte (feste 250px, kein Schrumpfen) — dort darf kein
            // `flex-1` dazwischenfunken.
            inShell ? 'min-h-0 flex-1 px-2 pb-2' : 'conversations p-3',
            // Anchor for the skeleton while it fades out (popLayout lifts it out of the flow).
            'relative',
        )}>
            {/* „Neuer Chat" direkt unter „Zum Dashboard" (das in der
                Sidebar-Kopfzeile der Shell steht): gleiche Menüzeile, gleicher
                Rahmen. Bewusst hier statt in `AppShell` — die Aktion und ihr
                Sperrzustand leben in diesem Component. Kein
                `AnimatedSidebarMenuButton`: das Portal hängt nicht im
                Panel-Kontext der Sidebar, dessen Hook würde werfen. */}
            <div className="shrink-0 px-1">
                {withRailTooltip(
                    <motion.button
                        type="button"
                        data-testid="chat-new-chat"
                        // Locked while no conversation is chosen yet, too: the
                        // first load usually lands on a fresh empty chat, and a
                        // button that is live for a moment and then locks
                        // flickers. Fading opacity makes the other case (an
                        // existing chat unlocks it) glide instead of jump.
                        disabled={!conversation || conversation.messages.length === 0}
                        aria-label={railCollapsed ? t('chatBotPage.newChat') : undefined}
                        onClick={() => createConversation()}
                        whileTap={{ scale: 0.98 }}
                        transition={SPRING_PRESS}
                        className="flex min-h-9 w-full min-w-0 items-center gap-2.5 overflow-hidden rounded-xl px-3 text-left text-sm font-medium text-muted-foreground outline-none transition-[color,background-color,opacity] duration-300 hover:bg-muted/50 hover:text-foreground focus-visible:bg-muted/70 focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        <span aria-hidden="true" className="grid size-5 shrink-0 place-items-center">
                            <MessageSquarePlus size={16} />
                        </span>
                        <span className={railLabelClass}>{t('chatBotPage.newChat')}</span>
                    </motion.button>,
                    t('chatBotPage.newChat'),
                )}
            </div>
            {/* „Suchen" klappt das Filterfeld darunter auf (Grid-Trick wie
                überall: 0fr↔1fr + Deckkraft). Zuklappen setzt den Filter
                zurück — ein unsichtbarer aktiver Filter wäre rätselhaft. */}
            {/* „Suchen" wird beim Klick inline zum Eingabefeld — gleiche
                Zeile, gleicher Platz; Escape oder das X schließen und setzen
                den Filter zurück. */}
            <div className="shrink-0 px-1">
                {searchOpen && !railCollapsed ? (
                    <div className="flex min-h-9 w-full min-w-0 items-center gap-2.5 rounded-xl bg-muted/50 px-3 text-sm">
                        <span aria-hidden="true" className="grid size-5 shrink-0 place-items-center text-muted-foreground">
                            <Search size={16} />
                        </span>
                        <input
                            ref={filterInputRef}
                            data-testid="chat-conversation-filter"
                            placeholder={t('ui.filterConversations')}
                            value={filterText}
                            onChange={(event) => setFilterText(event.target.value || '')}
                            onKeyDown={(event) => {
                                if (event.key === 'Escape') {
                                    event.preventDefault();
                                    setSearchOpen(false);
                                    setFilterText('');
                                }
                            }}
                            className="min-w-0 flex-1 border-0 bg-transparent p-0 font-[inherit] text-sm text-foreground outline-none placeholder:text-[var(--oa-fg-4)]"
                        />
                        <button
                            type="button"
                            aria-label={t('global.close')}
                            onClick={() => { setSearchOpen(false); setFilterText(''); }}
                            className="grid size-5 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:text-foreground"
                        >
                            <X size={14} />
                        </button>
                    </div>
                ) : (
                    withRailTooltip(
                        <motion.button
                            type="button"
                            data-testid="chat-search-toggle"
                            aria-expanded={searchOpen}
                            aria-label={railCollapsed ? t('chatBotPage.searchButton') : undefined}
                            onClick={() => {
                                // In der Symbolleiste hätte das Feld keinen
                                // Platz: der Klick öffnet die Leiste und
                                // gleich die Suche.
                                if (railCollapsed) {
                                    animatedSidebar?.setOpen(true);
                                }
                                setSearchOpen(true);
                            }}
                            whileTap={{ scale: 0.98 }}
                            transition={SPRING_PRESS}
                            className="flex min-h-9 w-full min-w-0 items-center gap-2.5 overflow-hidden rounded-xl px-3 text-left text-sm font-medium text-muted-foreground outline-none transition-colors hover:bg-muted/50 hover:text-foreground focus-visible:bg-muted/70 focus-visible:ring-2 focus-visible:ring-ring"
                        >
                            <span aria-hidden="true" className="grid size-5 shrink-0 place-items-center">
                                <Search size={16} />
                            </span>
                            <span className={railLabelClass}>{t('chatBotPage.searchButton')}</span>
                        </motion.button>,
                        t('chatBotPage.searchButton'),
                    )
                )}
            </div>
            {/* Skeleton and list cross-fade, so sections that turn out empty —
                e.g. no processes — fade away instead of vanishing in one frame. */}
            {/* `popLayout`: the skeleton fades out while the list fades in on
                top of it — `wait` left the rail empty in between. */}
            <AnimatePresence mode="popLayout" initial={false}>
            {!conversationsLoaded ? (
                // Lade-Skelett, bis die erste Listen-Antwort da ist: beide
                // Abschnitts-Überschriften mit Platzhalter-Zeilen in der
                // Geometrie der echten Zeilen (min-h-9, rounded-xl), damit
                // beim Umschalten nichts springt.
                <motion.div
                    key="skeleton"
                    data-testid="chat-conversation-skeleton"
                    exit={listReduceMotion ? undefined : { opacity: 0 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                    className={cn('flex min-h-0 flex-1 flex-col gap-4 transition-opacity duration-200', railCollapsed && 'pointer-events-none opacity-0')}
                    aria-hidden="true"
                >
                    {[
                        { heading: t('chatBotPage.processesHeading'), widths: [72, 58] },
                        { heading: t('chatBotPage.conversationsHeading'), widths: [65, 80, 55, 70, 60, 75] },
                    ].map((section) => (
                        <div key={section.heading} className="flex flex-col">
                            <div className="flex h-7 shrink-0 items-center px-2">
                                <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                                    {section.heading}
                                </span>
                            </div>
                            <div className="flex flex-col gap-0.5">
                                {section.widths.map((width, index) => (
                                    <div key={index} className="flex min-h-9 items-center gap-2.5 rounded-xl px-3 py-1.5">
                                        <Skeleton className="h-3.5 rounded-md" style={{ width: `${width}%` }} />
                                        <Skeleton className="ml-auto h-3 w-6 shrink-0 rounded-md" />
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </motion.div>
            ) : (
            <motion.div
                key="list"
                initial={listReduceMotion ? false : { opacity: 0 }}
                // Motion writes opacity inline, which beats the `opacity-0`
                // class — so the collapsed rail is driven here as well, or the
                // headings stay visible in the icon strip.
                animate={{ opacity: railCollapsed ? 0 : 1 }}
                transition={{ duration: listReduceMotion ? 0 : railCollapsed ? 0.2 : 0.25, ease: 'easeOut' }}
                className={cn('flex min-h-0 flex-1 flex-col gap-4', railCollapsed && 'pointer-events-none')}
                aria-hidden={railCollapsed}
            >
                {renderCategory(t('chatBotPage.processesHeading'), processConversations, { count: processTodoCount, fit: true })}
                {renderCategory(t('chatBotPage.endedProcessesHeading'), endedProcessConversations, { fit: true })}
                {/* „Konversationen" steht immer — die Überschrift trägt den
                    Plus-Knopf, der ohne sie bei leerer Liste verschwände. */}
                {renderCategory(t('chatBotPage.conversationsHeading'), otherConversations, {
                    action: newConversationButton,
                    always: true,
                    // The filter searches what is loaded; no paging while it is set (as in the app).
                    onEndReached: hasMoreConversations && !filterText ? loadMoreConversations : undefined,
                    loadingMore: loadingMoreConversations,
                })}
            </motion.div>
            )}
            </AnimatePresence>
        </div>
    );

    try {
        // Weicher Übergang zwischen Chat und Shell-Seiten: der Inhalt unterhalb
        // der geteilten Kopfzeile blendet ein.
        // `min-h-0` im Shell: ohne das wächst der Chat als Flex-Kind über den
        // Inset hinaus (automatische Mindesthöhe = Inhalt), seine Höhe bliebe
        // unbestimmt und die `100%`-Spalte unten könnte sie nicht auflösen —
        // auf schmalen Fenstern rutschte die Eingabezeile unter den Rand.
        return (
            <div className={cn('chatBot animate-in fade-in-0 duration-300', inShell && 'min-h-0')}>
                {/* Im Shell wandert die Liste in die Sidebar; ist deren Slot
                    gerade nicht im DOM (mobil bei geschlossenem Sheet),
                    entfällt sie, statt in den Chat zurückzufallen. */}
                {inShell
                    ? (chatSidebarSlot ? createPortal(conversationList, chatSidebarSlot) : null)
                    : conversationList}
                {/* A plain `div`: since A12 the morph into the question panel runs
                    through its own flying shell
                    (`src/guide/morph/AssistantMorphOverlay.tsx`) and no longer
                    through a shared `layoutId`. That sat on *this* scrolling
                    column and distorted its children while the spring was
                    running. */}
                <div
                    className={cn('chatContent', inShell && 'flex min-h-0 min-w-0 flex-1 flex-col')}
                    ref={chatContentRef}
                >
                    <ChatExchange showSuggestions={true}
                        // Kein Kundenlogo mitten auf der leeren Seite: auf dem
                        // dunklen Grund verschwinden dunkle Marken, und der
                        // open-agents-Look kennt keine Bühne vor dem ersten Satz.
                        showLogo={false}
                        conversation={conversation}
                        // Im Shell füllt die Spalte den Inset unter der
                        // Kopfzeile aus (Flex-Kette bis hierher, kein
                        // Viewport-Rechnen). Ohne Shell bleibt es die
                        // bisherige Rechnung mit dem 48px hohen SiteHeader.
                        height={inShell ? '100%' : 'calc(100vh - 88px)'}
                        resourceSelected={properties.resourceSelected}
                        updateConversation={updateConversation}
                        createConversation={createConversation}
                        onCompleteTaskClosed={onCompleteTaskClosed}
                        onStreamComplete={() =>
                            refreshConversations(undefined, { silent: true })
                        }
                        onRunningConversationsChange={setRunningConversationIds}
                        reloadConversation={reloadConversation}
                        inputFocusTrigger={inputFocusTrigger}
                        scrollToEndTrigger={scrollToEndTrigger}
                        onWorklistTaskSelect={handleWorklistTaskSelect}
                        customerTools={customerTools}
                        customerToolsLoading={customerToolsLoading}
                        selectedToolId={selectedToolId}
                        onToolChange={handleToolChange}
                    />
                </div>
                {!hideDialog && (
                <Dialog
                    open
                    onOpenChange={(next) => { if (!next) toggleHideDialog(); }}
                >
                    <DialogContent className="max-w-none" style={{ minWidth: 600 }}>
                        <DialogHeader>
                            <DialogTitle>{clientActionTitle(skillAgentResponse)}</DialogTitle>
                        </DialogHeader>
                        {skillAgentResponse && skillAgentResponse.skill ?
                            <>
                                {skillAgentResponse.message}
                                {(() => {
                                    switch (skillAgentResponse.skill.id) {
                                        case 'clientAction/wayfinding': return <img src="./images/navigation.png" alt="navigation" className="mt-2.5 w-[300px]" />
                                        case 'clientAction/showDetails': return <img src="./images/room.png" alt="room" className="mt-2.5 w-[300px]" />
                                        default: return <></>
                                    }
                                })()}</>
                            :
                            <></>}
                        <DialogFooter>
                            <Button type="button" onClick={toggleHideDialog}>Close</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
                )}
            </div >);
    } catch (error) {
        console.error('Error in ChatBot:', error);
        return <div>Error loading chatbot</div>;
    }
}
